import {match} from 'ts-pattern';

import {useContext, useSyncExternalStore} from 'react';

import {always, isNil} from 'ramda';

import {DeviceContext} from '../components/DeviceProvider/useDevice';
import {Device} from '../types/Device';
import {ValueByDevice} from '../types/ValueByDevice';
import {foundationTheme} from '../utils/foundationTheme';
import {getValueByDevice} from '../utils/getValueByDevice';

type DeviceMediaQuery = {
  device: Device;
  queryList: MediaQueryList;
  listener: (event: MediaQueryListEvent) => void;
};

const DEVICE_MEDIA_QUERIES: ReadonlyArray<{device: Device; mediaQuery: string}> = [
  {device: 'mobile', mediaQuery: foundationTheme.devices.mobile},
  {device: 'tablet', mediaQuery: foundationTheme.devices.tablet},
  {device: 'notebook', mediaQuery: foundationTheme.devices.notebook},
  {device: 'desktop', mediaQuery: foundationTheme.devices.desktop},
  {device: 'printer', mediaQuery: foundationTheme.devices.printer},
];

const deviceStoreSubscribers = new Set<() => void>();
let deviceMediaQueries: DeviceMediaQuery[] = [];
let currentDevice: Device | undefined;

function getCurrentDevice(): Device {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'desktop';
  }

  return (
    DEVICE_MEDIA_QUERIES.find(({mediaQuery}) => window.matchMedia(mediaQuery)?.matches)?.device ??
    'desktop'
  );
}

function getDeviceSnapshot(): Device {
  currentDevice ??= getCurrentDevice();
  return currentDevice;
}

function getServerDeviceSnapshot(): Device {
  return 'desktop';
}

function startDeviceStore() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }

  currentDevice = getCurrentDevice();
  deviceMediaQueries = DEVICE_MEDIA_QUERIES.flatMap(({device, mediaQuery}) => {
    const queryList = window.matchMedia(mediaQuery);
    if (isNil(queryList)) {
      return [];
    }

    const listener = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        return;
      }

      currentDevice = device;
      deviceStoreSubscribers.forEach((subscriber) => subscriber());
    };

    if (typeof queryList.addEventListener === 'function') {
      queryList.addEventListener('change', listener);
    } else if (typeof queryList.addListener === 'function') {
      queryList.addListener(listener);
    } else {
      return [];
    }

    return [{device, queryList, listener}];
  });
}

function stopDeviceStore() {
  deviceMediaQueries.forEach(({queryList, listener}) => {
    if (typeof queryList.removeEventListener === 'function') {
      queryList.removeEventListener('change', listener);
    } else if (typeof queryList.removeListener === 'function') {
      queryList.removeListener(listener);
    }
  });
  deviceMediaQueries = [];
  currentDevice = undefined;
}

function subscribeToDeviceStore(subscriber: () => void) {
  deviceStoreSubscribers.add(subscriber);
  if (deviceStoreSubscribers.size === 1) {
    startDeviceStore();
  }

  return () => {
    deviceStoreSubscribers.delete(subscriber);
    if (deviceStoreSubscribers.size === 0) {
      stopDeviceStore();
    }
  };
}

function subscribeToNothing() {
  return () => undefined;
}

type PrimitiveValue<T> = T extends ValueByDevice<infer P> ? P : Exclude<T, Array<unknown>>;

export function getResponsivePropValue<T>(device: Device, value: T): PrimitiveValue<T> {
  if (Array.isArray(value)) {
    const arrValue = value as Array<any>; // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
    return match(arrValue.length)
      .with(1, always(getValueByDevice(device, arrValue[0])))
      .with(2, always(getValueByDevice(device, arrValue[0], arrValue[1])))
      .with(3, always(getValueByDevice(device, arrValue[0], arrValue[1], arrValue[2])))
      .with(4, always(getValueByDevice(device, arrValue[0], arrValue[1], arrValue[2], arrValue[3])))
      .with(
        5,
        always(
          getValueByDevice(device, arrValue[0], arrValue[1], arrValue[2], arrValue[3], arrValue[4])
        )
      )
      .otherwise(always(value as PrimitiveValue<T>));
  } else {
    return value as PrimitiveValue<T>;
  }
}

/**
 * Platform internal tool to handle possible responsive props like spacing?: Integer | ValueByDevice<Integer>;
 */
export function useResponsivePropValue<T>(value: T): PrimitiveValue<T> {
  const contextDevice = useContext(DeviceContext);
  const fallbackDevice = useSyncExternalStore(
    isNil(contextDevice) ? subscribeToDeviceStore : subscribeToNothing,
    getDeviceSnapshot,
    getServerDeviceSnapshot
  );

  return getResponsivePropValue(contextDevice ?? fallbackDevice, value);
}
